import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Wallet as WalletIcon, Lock, TrendingUp, ArrowUpRight, ArrowDownLeft,
  Clock, CheckCircle, Shield, Unlock, FileText, Plus, X,
  CreditCard, AlertCircle, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Wallet = () => {
  const { getAuthHeaders } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [lockedDetails, setLockedDetails] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [animateTimeline, setAnimateTimeline] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFundModal, setShowFundModal] = useState(false);

  useEffect(() => { fetchWalletData(); }, []);
  useEffect(() => {
    if (searchParams.get('fund') === '1') {
      setShowFundModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWalletData = async () => {
    try {
      const headers = getAuthHeaders();
      const [walletRes, txRes, lockedRes, depRes] = await Promise.all([
        axios.get(`${API_URL}/api/wallet`, { headers }),
        axios.get(`${API_URL}/api/wallet/transactions`, { headers }),
        axios.get(`${API_URL}/api/wallet/locked-details`, { headers }),
        axios.get(`${API_URL}/api/wallet/my-deposits`, { headers }),
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data);
      setLockedDetails(lockedRes.data);
      setDeposits(depRes.data);
      setTimeout(() => setAnimateTimeline(true), 100);
    } catch (error) {
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency', currency: 'NGN', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount).replace('NGN', '\u20a6');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-NG', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit': case 'payout': case 'security_release': case 'qualification_release':
        return <ArrowDownLeft className="text-[#2272C5]" size={18} />;
      case 'contribution':
        return <ArrowUpRight className="text-amber-500" size={18} />;
      case 'lock': case 'security_hold':
        return <Lock className="text-amber-500" size={18} />;
      default:
        return <Clock className="text-slate-400" size={18} />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'credit': case 'payout': case 'security_release': case 'qualification_release':
        return 'text-[#2272C5]';
      case 'contribution': case 'lock': case 'security_hold':
        return 'text-amber-600';
      default:
        return 'text-slate-600';
    }
  };

  const getTransactionStatus = (type) => {
    switch (type) {
      case 'security_hold':
        return { label: 'Locked', color: 'bg-amber-100 text-amber-700' };
      case 'security_release': case 'qualification_release':
        return { label: 'Available', color: 'bg-[#2272C5]/10 text-[#2272C5]' };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-48 w-full rounded-2xl mb-6" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
        <BottomNav />
      </div>
    );
  }

  const items = lockedDetails ? lockedDetails.locked_items : [];
  const hasLockedFunds = wallet && wallet.locked_balance > 0;
  const pendingDeposits = deposits.filter(d => d.status === 'pending');

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="wallet-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0B1F3A]">Wallet</h1>
          <Button
            onClick={() => setShowFundModal(true)}
            className="bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white rounded-xl"
            size="sm"
            data-testid="fund-wallet-button"
          >
            <Plus size={16} className="mr-1"/>Fund Wallet
          </Button>
        </div>

        {/* Balance Card */}
        <Card className="balance-card mb-6" data-testid="wallet-balance-card">
          <div className="flex items-center gap-2 mb-1">
            <WalletIcon size={16} />
            <span className="text-sm text-white/80">Total Balance</span>
          </div>
          <h2 className="text-3xl font-bold mb-6" data-testid="wallet-total-balance">
            {formatCurrency(wallet?.total_balance || 0)}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-4 rounded-xl bg-[#2272C5]/30 border border-[#38B6CC]/30 cursor-help" data-testid="wallet-available">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp size={16} className="text-[#38B6CC]" />
                      <span className="text-sm text-[#38B6CC]">Available</span>
                    </div>
                    <p className="text-xl font-bold">{formatCurrency(wallet?.available_balance || 0)}</p>
                    <p className="text-xs text-white/60 mt-1">Ready to spend or save</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <p className="text-sm">This is the amount you can freely use for contributions, transfers, or withdrawals.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-4 rounded-xl bg-amber-500/20 border border-amber-400/30 cursor-help" data-testid="wallet-locked">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock size={16} className="text-amber-400" />
                      <span className="text-sm text-amber-300">Locked</span>
                    </div>
                    <p className="text-xl font-bold">{formatCurrency(wallet?.locked_balance || 0)}</p>
                    <p className="text-xs text-white/60 mt-1">Held for group safety</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px]">
                  <p className="text-sm">These funds protect your savings groups. They are returned to you automatically when conditions are met.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </Card>

        {/* Pending Deposits Notice */}
        {pendingDeposits.length > 0 && (
          <Card className="glass-card mb-4 border-2 border-amber-200 bg-amber-50" data-testid="pending-deposits-notice">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={16} className="text-amber-600" />
                <span className="text-sm font-bold text-amber-800">Pending Deposits ({pendingDeposits.length})</span>
              </div>
              {pendingDeposits.map(dep => (
                <div key={dep.id} className="flex items-center justify-between p-2 rounded-lg bg-white/60 mb-1.5 last:mb-0">
                  <div>
                    <p className="text-sm font-medium text-[#0B1F3A]">{formatCurrency(dep.amount)}</p>
                    <p className="text-[10px] text-slate-500">Ref: {dep.reference}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pending Review</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Fund Release Timeline */}
        {hasLockedFunds && (
          <Card className="glass-card mb-6" data-testid="fund-release-timeline">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-[#0B1F3A] flex items-center gap-2">
                <Clock size={18} className="text-[#2272C5]" />
                Fund Release Timeline
              </CardTitle>
              <p className="text-sm text-slate-500">Track when your locked funds will become available</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item, idx) => {
                const isQual = item.type === 'qualification_deposit';
                const progress = item.progress_percent || 0;
                const currentCycle = item.current_cycle || 0;
                const releaseCycle = item.release_cycle || 0;
                const isReleased = item.group_status === 'completed';

                return (
                  <div key={idx} className="p-4 rounded-xl bg-slate-50 border border-slate-200 transition-all duration-500"
                    style={{ opacity: animateTimeline ? 1 : 0, transform: animateTimeline ? 'translateY(0)' : 'translateY(8px)', transitionDelay: `${idx * 120}ms` }}
                    data-testid={`timeline-item-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {isQual ? <Shield size={14} className="text-[#0B1F3A]" /> : <Lock size={14} className="text-amber-600" />}
                        <span className="text-sm font-semibold text-[#0B1F3A]">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-[#0B1F3A]">{formatCurrency(item.amount)}</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">{item.group_name}</p>
                    <div className="relative h-2.5 rounded-full bg-slate-200 overflow-hidden mb-2">
                      <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out ${isReleased ? 'bg-emerald-500' : 'bg-[#2272C5]'}`}
                        style={{ width: animateTimeline ? `${Math.min(progress, 100)}%` : '0%' }} />
                    </div>
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-slate-500">Cycle {currentCycle} of {releaseCycle}</span>
                      <span className={`font-bold ${isReleased ? 'text-emerald-600' : 'text-[#2272C5]'}`}>{isReleased ? 'Released' : `${progress}%`}</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      {isReleased ? <CheckCircle size={12} className="text-emerald-600 mt-0.5 flex-shrink-0" /> : <Unlock size={12} className="text-emerald-600 mt-0.5 flex-shrink-0" />}
                      <p className="text-xs text-emerald-700 font-medium">{item.release_condition}</p>
                    </div>
                  </div>
                );
              })}
              {items.length === 0 && <p className="text-sm text-slate-500 text-center py-2">No locked items found</p>}
              <div className="p-3 rounded-xl bg-[#0B1F3A]/5 border border-[#0B1F3A]/10 flex items-center gap-2">
                <Shield size={14} className="text-[#2272C5] flex-shrink-0" />
                <span className="text-xs text-slate-600">Your locked funds are fully protected and will be returned automatically. No money is ever lost.</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transactions */}
        <Card className="glass-card" data-testid="transactions-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[#0B1F3A]">Transaction History</CardTitle>
              <Button variant="outline" size="sm" className="rounded-xl text-xs"
                onClick={() => {
                  const url = `${API_URL}/api/wallet/statement`;
                  fetch(url, { headers: getAuthHeaders() })
                    .then(res => res.blob())
                    .then(blob => {
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `AegisPay_Statement.pdf`;
                      a.click();
                      toast.success('Statement downloaded');
                    })
                    .catch(() => toast.error('Failed to download statement'));
                }}
                data-testid="download-statement-button"
              >
                <FileText size={14} className="mr-1" />PDF Statement
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="in">In</TabsTrigger>
                <TabsTrigger value="out">Out</TabsTrigger>
              </TabsList>
              <TabsContent value="all" className="mt-0">
                <TransactionList transactions={transactions} formatCurrency={formatCurrency} formatDate={formatDate} getTransactionIcon={getTransactionIcon} getTransactionColor={getTransactionColor} getTransactionStatus={getTransactionStatus} />
              </TabsContent>
              <TabsContent value="in" className="mt-0">
                <TransactionList transactions={transactions.filter(t => ['credit','payout','security_release','qualification_release'].includes(t.type))} formatCurrency={formatCurrency} formatDate={formatDate} getTransactionIcon={getTransactionIcon} getTransactionColor={getTransactionColor} getTransactionStatus={getTransactionStatus} />
              </TabsContent>
              <TabsContent value="out" className="mt-0">
                <TransactionList transactions={transactions.filter(t => ['contribution','lock','security_hold'].includes(t.type))} formatCurrency={formatCurrency} formatDate={formatDate} getTransactionIcon={getTransactionIcon} getTransactionColor={getTransactionColor} getTransactionStatus={getTransactionStatus} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Fund Wallet Modal */}
      {showFundModal && (
        <FundWalletModal
          onClose={() => setShowFundModal(false)}
          onSuccess={() => { setShowFundModal(false); fetchWalletData(); }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      <BottomNav />
    </div>
  );
};

/* ─── FUND WALLET MODAL ──────────────────────────────── */
const FundWalletModal = ({ onClose, onSuccess, getAuthHeaders }) => {
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const quickAmounts = [1000, 2000, 5000, 10000, 20000, 50000];

  const handleSubmit = async () => {
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount < 100) { toast.error('Minimum amount is ₦100'); return; }
    if (!reference.trim() || reference.trim().length < 3) { toast.error('Please enter a valid transfer reference'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/wallet/deposit-request`, {
        amount: numAmount,
        reference: reference.trim(),
      }, { headers: getAuthHeaders() });
      toast.success('Deposit request submitted! Awaiting admin approval.');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit deposit');
    } finally {
      setSubmitting(false);
    }
  };

  const bankDetails = {
    bankName: 'AegisPay Holdings',
    accountNumber: '0123456789',
    bankBranch: 'Zenith Bank',
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="fund-wallet-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#0B1F3A]">Fund Your Wallet</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="close-fund-modal">
            <X size={20} className="text-slate-500"/>
          </button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-[#0B1F3A] text-white">
              <p className="text-sm font-medium text-white/70 mb-1">Transfer to this account</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Bank</span>
                  <span className="text-sm font-bold">{bankDetails.bankBranch}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Account Name</span>
                  <span className="text-sm font-bold">{bankDetails.bankName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Account Number</span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold font-mono">{bankDetails.accountNumber}</span>
                    <button onClick={() => copyToClipboard(bankDetails.accountNumber)} className="p-1 rounded-md hover:bg-white/10" data-testid="copy-account-number">
                      <Copy size={14} className="text-white/70"/>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#0B1F3A] block mb-2">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 text-lg font-bold focus:outline-none focus:border-[#2272C5] focus:ring-2 focus:ring-[#2272C5]/20"
                data-testid="deposit-amount-input"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {quickAmounts.map(qa => (
                  <button key={qa} onClick={() => setAmount(String(qa))} className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${String(qa) === amount ? 'bg-[#0B1F3A] text-white border-[#0B1F3A]' : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-[#2272C5]'}`} data-testid={`quick-amount-${qa}`}>
                    {new Intl.NumberFormat('en-NG').format(qa)}
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={() => { if (!amount || parseFloat(amount) < 100) { toast.error('Minimum ₦100'); return; } setStep(2); }} className="w-full bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white rounded-xl py-3 text-base font-bold" data-testid="proceed-to-reference">
              <CreditCard size={18} className="mr-2"/>I've Made the Transfer
            </Button>

            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0"/>
              <p className="text-xs text-amber-700">Transfer the exact amount to the account above, then proceed to enter your transfer reference for verification.</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-emerald-50 border-2 border-emerald-200">
              <p className="text-sm text-emerald-700 font-medium">Amount to be credited</p>
              <p className="text-2xl font-bold text-emerald-800">{new Intl.NumberFormat('en-NG', {style:'currency', currency:'NGN', minimumFractionDigits:0}).format(parseFloat(amount) || 0).replace('NGN','\u20a6')}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-[#0B1F3A] block mb-2">Bank Transfer Reference</label>
              <input
                type="text"
                value={reference}
                onChange={e => setReference(e.target.value)}
                placeholder="e.g. TRF-2026-123456 or session ID"
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 font-mono focus:outline-none focus:border-[#2272C5] focus:ring-2 focus:ring-[#2272C5]/20"
                data-testid="deposit-reference-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">Enter the transaction reference from your bank app or receipt</p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-xl">Back</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold" data-testid="submit-deposit-request">
                {submitting ? 'Submitting...' : 'Submit for Approval'}
              </Button>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-200">
              <Shield size={14} className="text-blue-600 mt-0.5 flex-shrink-0"/>
              <p className="text-xs text-blue-700">Your deposit will be reviewed and approved by our team, usually within 15 minutes during business hours.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── TRANSACTION LIST ───────────────────────────────── */
const TransactionList = ({ transactions, formatCurrency, formatDate, getTransactionIcon, getTransactionColor, getTransactionStatus }) => {
  if (transactions.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock size={32} className="mx-auto text-slate-300 mb-2" />
        <p className="text-slate-500">No transactions yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {transactions.map((tx) => {
        const status = getTransactionStatus(tx.type);
        return (
          <div key={tx.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`transaction-${tx.id}`}>
            <div className="p-2 rounded-xl bg-white shadow-sm">{getTransactionIcon(tx.type)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#0B1F3A] text-sm truncate">{tx.description}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-slate-500">{formatDate(tx.created_at)}</span>
                {tx.status === 'completed' && !status && <CheckCircle size={12} className="text-[#2272C5]" />}
                {status && <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>{status.label}</span>}
              </div>
            </div>
            <p className={`font-semibold ${getTransactionColor(tx.type)}`}>
              {['credit','payout','security_release','qualification_release'].includes(tx.type) ? '+' : '-'}{formatCurrency(tx.amount)}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default Wallet;
